import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import {
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  Paper,
  IconButton,
} from '@mui/material'
import { DataTable } from '@components/tables'
import { findAllRooms, removeReception } from '@state/thunks/roomThunk'
import { useSelector } from '@state/store'
import { GridColumns, GridRowId } from '@mui/x-data-grid-pro'
import { findAll as findAllSites } from '@state/thunks/siteThunk'
import { Actions } from '@state/actions'
import { removeSites } from '@state/reducers/siteReducer'
import { ReceptionsForm } from './ReceptionsForm'
import { Reception, RoomType, Site } from '@services/api'
import { EditIcon } from 'lucide-react'
import { DeleteConfirmationDialog } from '@components/dialogs/DeleteConfirmationDialog'

export default function Receptions() {
  const dispatch = useDispatch()
  const [openAddDialog, setOpenAddDialog] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [selectedRows, setSelectedRows] = useState<number[]>([])
  const [editingReception, setEditingReception] = useState<
    Reception | undefined
  >(undefined)

  const { loading, rooms } = useSelector(({ room, loading }) => ({
    rooms: room.rooms.filter((_room) => _room.type === RoomType.Reception),
    loading:
      loading[Actions.UPDATE_RECEPTION] ||
      loading[Actions.CREATE_RECEPTION] ||
      loading[Actions.ROOM_FIND_ALL],
  }))
  const columns: GridColumns = [
    { field: 'label', headerName: 'Label', flex: 1 },
    { field: 'code', headerName: 'Code', flex: 1 },
    {
      field: 'site',
      headerName: 'Site',
      flex: 1,
      valueFormatter: (params) => {
        return (params.value as unknown as Site)?.label
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      align: 'right',
      renderCell: (params) => {
        return (
          <IconButton
            size="small"
            onClick={() => {
              setEditingReception(params.row)
              setOpenAddDialog(true)
            }}
          >
            <EditIcon />
          </IconButton>
        )
      },
    },
  ]

  const onRefresh = useCallback(() => {
    dispatch(findAllRooms())
    dispatch(findAllSites())
  }, [dispatch])

  useEffect(() => {
    onRefresh()
    return () => {
      dispatch(removeSites())
    }
  }, [dispatch, onRefresh])

  const _onSelectionChange = (selection: GridRowId[]) => {
    setSelectedRows(selection as number[])
  }
  const _onDeleteConfirm = () => {
    dispatch(removeReception({ id: selectedRows }))
    setIsDeleteDialogOpen(false)
  }

  return (
    <Container maxWidth={false} style={{ height: '93%', padding: '0px 24px' }}>
      <Paper
        elevation={2}
        style={{ width: '100%', borderRadius: '0px 15px 15px 15px' }}
      >
        <DataTable
          rows={rooms}
          columns={columns}
          loading={loading}
          toolBarProps={{
            title: 'reception',
            icon: 'fab fa-uikit',
            onAdd: () => {
              setEditingReception(undefined)
              setOpenAddDialog(true)
            },
            onRefresh,
            onDelete: () => {
              selectedRows.length
                ? setIsDeleteDialogOpen(true)
                : alert('please select at least one reception')
            },
          }}
          detailPanelHeight={400}
          checkboxSelection
          onSelectionModelChange={_onSelectionChange}
        />
      </Paper>

      <Dialog
        open={openAddDialog}
        onClose={() => setOpenAddDialog(false)}
        maxWidth="lg"
        TransitionProps={{
          onExited: () => setEditingReception(undefined),
        }}
      >
        <DialogTitle>
          {editingReception
            ? 'Modifier une réception'
            : 'Ajouter une réception'}
        </DialogTitle>
        <DialogContent>
          <ReceptionsForm
            key={editingReception?.id ?? 'new'}
            onClose={() => setOpenAddDialog(false)}
            row={editingReception}
          />
        </DialogContent>
      </Dialog>

      <DeleteConfirmationDialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={_onDeleteConfirm}
        title="Confirmer la suppression"
        description="Etes vous sur de vouloir supprimer les réceptions selectionnées ?"
      />
    </Container>
  )
}
